import React, { useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { useMatchMedia, useIsomorphicLayoutEffect } from 'web-ui-kit/hooks';

function _taggedTemplateLiteral(strings, raw) {
  if (!raw) {
    raw = strings.slice(0);
  }

  return Object.freeze(Object.defineProperties(strings, {
    raw: {
      value: Object.freeze(raw)
    }
  }));
}

function _slicedToArray(arr, i) {
  return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _unsupportedIterableToArray(arr, i) || _nonIterableRest();
}

function _arrayWithHoles(arr) {
  if (Array.isArray(arr)) return arr;
}

function _iterableToArrayLimit(arr, i) {
  if (typeof Symbol === "undefined" || !(Symbol.iterator in Object(arr))) return;
  var _arr = [];
  var _n = true;
  var _d = false;
  var _e = undefined;

  try {
    for (var _i = arr[Symbol.iterator](), _s; !(_n = (_s = _i.next()).done); _n = true) {
      _arr.push(_s.value);

      if (i && _arr.length === i) break;
    }
  } catch (err) {
    _d = true;
    _e = err;
  } finally {
    try {
      if (!_n && _i["return"] != null) _i["return"]();
    } finally {
      if (_d) throw _e;
    }
  }

  return _arr;
}

function _unsupportedIterableToArray(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(n);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen);
}

function _arrayLikeToArray(arr, len) {
  if (len == null || len > arr.length) len = arr.length;

  for (var i = 0, arr2 = new Array(len); i < len; i++) {
    arr2[i] = arr[i];
  }

  return arr2;
}

function _nonIterableRest() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}

var _templateObject;

function Modal() {
  var _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
      _ref$className = _ref.className,
      className = _ref$className === void 0 ? '' : _ref$className,
      children = _ref.children,
      _ref$onOverlayClick = _ref.onOverlayClick,
      onOverlayClick = _ref$onOverlayClick === void 0 ? function () {} : _ref$onOverlayClick,
      _ref$useLockBodyScrol = _ref.useLockBodyScroll,
      useLockBodyScroll = _ref$useLockBodyScrol === void 0 ? useDefaultLockBodyScroll : _ref$useLockBodyScrol,
      _ref$useStrictPointer = _ref.useStrictPointerEvents,
      useStrictPointerEvents = _ref$useStrictPointer === void 0 ? true : _ref$useStrictPointer;

  useLockBodyScroll();
  var node = useRef();

  var handleOverlayClick = function handleOverlayClick(event) {
    var isOverlayClick = node.current && event.target === node.current;

    if (isOverlayClick || !useStrictPointerEvents) {
      onOverlayClick();
    }
  };

  return (
    /*#__PURE__*/
    createPortal(
    /*#__PURE__*/
    React.createElement(Overlay, {
      className: className,
      "data-testid": "modal-overlay",
      onClick: handleOverlayClick,
      ref: node
    }, children), document.body)
  );
}
/**
 * Fixed positioned overlay container that fills
 * the entire available width and height.
 * These style rules can be easily customized using
 * the styled(Modal) constructor.
 */


var Overlay = styled.div(_templateObject || (_templateObject = _taggedTemplateLiteral(["\n  position: fixed;\n  top: 0;\n  left: 0;\n  bottom: 0;\n  right: 0;\n  max-height: 100vh;\n  z-index: 1;\n\n  background-color: rgba(0, 0, 0, 0.4);\n  box-sizing: border-box;\n"])));
/**
 * Custom hook that locks body scroll including on iOS devices.
 * Defined here instead of in separate module because it does not
 * really make sense to lock body scroll outside of a modal use case.
 */

function useDefaultLockBodyScroll() {
  var _ref2 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
      _ref2$queryStringEnab = _ref2.queryStringEnableFor,
      queryStringEnableFor = _ref2$queryStringEnab === void 0 ? null : _ref2$queryStringEnab; // If we only want to enable the lock during specific media query matches
  // then we disable the functionality until we get a media query match


  var _useState = useState(!queryStringEnableFor),
      _useState2 = _slicedToArray(_useState, 2),
      mediaQueryMatches = _useState2[0],
      setMediaQueryMatches = _useState2[1]; // Callback for the match media to update our state variable


  var onMatchMediaChange = useCallback(function (matches) {
    setMediaQueryMatches(matches);
  }); // Track media matches

  useMatchMedia({
    queryString: queryStringEnableFor,
    onChanged: onMatchMediaChange
  });
  var overflow = useRef();
  var position = useRef();
  var top = useRef();
  var width = useRef();
  var pageYOffset = useRef();
  useIsomorphicLayoutEffect(function () {
    if (typeof window !== 'undefined' && typeof document !== 'undefined') {
      // Store the current styles and scroll offset.
      overflow.current = document.body.style.overflow;
      position.current = document.body.style.position;
      top.current = document.body.style.top;
      width.current = document.body.style.width;
      pageYOffset.current = window.pageYOffset;

      if (mediaQueryMatches) {
        // This locks body scroll on everything except iOS.
        document.body.style.overflow = 'hidden';
      } // iOS requires extra styling. We do not want to apply this extra
      // styling when its not needed because it can cause unexpected
      // side effects for no benefit. So we need to first detect if we
      // are currently running on an iOS device.
      // https://stackoverflow.com/questions/9038625/detect-if-device-is-ios


      var isIOS = navigator && navigator.platform && (/iPad|iPhone|iPod/.test(navigator.platform) || // iOS 12 and below
      navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1 // iOS 13
      ) && !window.MSStream; // microsoft tried to fool gmail by injecting iPhone in their user agent;

      if (isIOS && mediaQueryMatches) {
        // Setting position:fixed is the only way to ensure the underneath modal content does not
        // scroll on iOS. While it accomplishes this goal it has the unfortunate side effect of
        // scrolling the underneath modal content to the top of the page. Note that there are some
        // claims that iOS 13 does not have this issue but those claims are false. In particular,
        // the underneath content WILL scroll when the browser chrome is hidden.
        document.body.style.position = 'fixed'; // This counteracts the undersired scroll to top behavior by re-applying the scroll.

        document.body.style.top = -pageYOffset.current + 'px'; // When we set position:fixed we also need to ensure the body is full width
        // otherwise elements may jump horizontally.

        document.body.style.width = '100%';
      }

      return function () {
        // Restore all the styles we changed when displaying the modal.
        document.body.style.overflow = overflow.current;

        if (isIOS) {
          document.body.style.position = position.current;
          document.body.style.top = top.current;
          document.body.style.width = width.current;

          if (typeof window.scrollTo === 'function') {
            window.scrollTo(0, pageYOffset.current);
          }
        }
      };
    }
  }, [mediaQueryMatches]);
}

export default Modal;
export { useDefaultLockBodyScroll };